import React, { useState, useEffect, useCallback } from "react";
import {
  Server,
  Plug,
  Power,
  RotateCw,
  ArrowLeft,
  Wrench,
  Database,
  MessageSquare,
  AlertTriangle,
  FolderOpen,
  CheckCircle2,
  XCircle,
  Search,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { GatewayServersApi, GatewayServerConfigApi } from "../Api/gatewayApi";
import { timeAgo, apiError } from "../utils/gatewayUtils";
import { useToast } from "../../../../hooks/use-toast";
import { cn } from "../../../lib/utils";

function priorityFilter<T>(items: T[], query: string, getName: (item: T) => string): T[] {
  if (!query.trim()) return items;
  const q = query.toLowerCase().trim();
  return items.filter((item) => getName(item).toLowerCase().startsWith(q));
}


interface ServerInfo {
  configKey: string;
  serverName?: string;
  configName?: string;
  status: string;
  active: boolean;
  toolCount?: number;
  resourceCount?: number;
  promptCount?: number;
  serverVersion?: string;
  version?: string;
  lastActivity?: string;
}

interface SchemaProp {
  type?: string;
  description?: string;
}

interface InputSchema {
  properties?: Record<string, SchemaProp>;
  required?: string[];
}

interface Tool {
  name: string;
  description?: string;
  inputSchema?: InputSchema;
}

interface Resource {
  name: string;
  description?: string;
  uri?: string;
  mimeType?: string;
}

interface PromptArg {
  name: string;
  required?: boolean;
  description?: string;
}

interface Prompt {
  name: string;
  description?: string;
  arguments?: PromptArg[];
}


function parseServerName(raw: string): { primary: string; sub?: string } {
  const trimmed = (raw || "unknown").trim();
  const match = trimmed.match(/^(.*?)(\s*\(([^)]+)\))$/);
  if (!match) return { primary: trimmed };
  return {
    primary: (match[1] || "").trim() || trimmed,
    sub: (match[3] || "").trim(),
  };
}


function ServerNameDisplay({ name }: { name: string }) {
  const { primary, sub } = parseServerName(name);
  return (
    <span className="flex items-center gap-1 min-w-0">
      <span className="font-semibold text-foreground truncate">{primary}</span>
      {sub && <span className="text-xs text-muted-foreground shrink-0">({sub})</span>}
    </span>
  );
}

function ConnectionBadge({ active }: { active: boolean }) {
  return (
    <Badge
      className={cn(
        "flex items-center gap-1.5 text-xs font-medium border",
        active
          ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
          : "bg-red-500/15 text-red-400 border-red-500/30"
      )}
    >
      <span
        className={cn(
          "w-1.5 h-1.5 rounded-full",
          active ? "bg-emerald-400" : "bg-red-400"
        )}
      />
      {active ? "CONNECTED" : "DISCONNECTED"}
    </Badge>
  );
}

function CapabilityPill({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-0.5 rounded-lg bg-muted/60 px-3 py-2 flex-1">
      <div className="flex items-center gap-1 text-muted-foreground">
        {icon}
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <span className="text-lg font-bold text-foreground">{value}</span>
    </div>
  );
}

function SchemaTable({ schema }: { schema: InputSchema }) {
  const props = schema.properties || {};
  const required = schema.required || [];
  const entries = Object.entries(props);
  if (entries.length === 0) return null;

  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow className="border-border hover:bg-transparent">
            <TableHead className="text-xs text-muted-foreground h-7 py-1">Property</TableHead>
            <TableHead className="text-xs text-muted-foreground h-7 py-1">Type</TableHead>
            <TableHead className="text-xs text-muted-foreground h-7 py-1">Required</TableHead>
            <TableHead className="text-xs text-muted-foreground h-7 py-1">Description</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map(([name, prop]) => (
            <TableRow key={name} className="border-border hover:bg-muted/60">
              <TableCell className="font-mono text-xs text-primary py-1.5">{name}</TableCell>
              <TableCell className="text-xs text-amber-400 py-1.5">{prop.type || "any"}</TableCell>
              <TableCell className="py-1.5">
                {required.includes(name) ? (
                  <Badge className="bg-red-500/15 text-red-400 border-red-500/30 text-xs border py-0">YES</Badge>
                ) : null}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground py-1.5">{prop.description || ""}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function EmptyState({ message, sub }: { message: string; sub?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <FolderOpen className="w-10 h-10 text-muted-foreground/70" />
      <p className="text-muted-foreground text-sm font-medium">{message}</p>
      {sub && <p className="text-muted-foreground text-xs">{sub}</p>}
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <AlertTriangle className="w-10 h-10 text-amber-500" />
      <p className="text-muted-foreground text-sm font-medium">Failed to load</p>
      <p className="text-muted-foreground text-xs font-mono">{message}</p>
    </div>
  );
}


function ToolsTab({ configKey }: { configKey: string }) {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    GatewayServersApi.tools(configKey)
      .then((data) => {
        if (!cancelled) setTools(Array.isArray(data) ? data : []);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(apiError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [configKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
        <div className="w-4 h-4 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
        <span className="text-sm">Loading tools…</span>
      </div>
    );
  }
  if (error) return <ErrorState message={error} />;
  if (tools.length === 0) return <EmptyState message="No tools registered" />;

  const filtered = priorityFilter(tools, search, (t) => t.name);

  return (
    <div className="space-y-4 mt-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <input
          type="text"
          placeholder={`Search ${tools.length} tools...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2.5 bg-muted/60 border border-border rounded-lg text-xs text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-primary/50 transition-colors"
        />
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No tools matching "{search}"</p>
      ) : (
      <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
      {filtered.map((tool) => (
        <Card key={tool.name} className="bg-card border-border hover:border-border transition-all">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-mono text-primary flex items-center gap-2">
              <Wrench className="w-3.5 h-3.5 shrink-0" />
              {tool.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-xs text-muted-foreground mb-1">{tool.description || "No description"}</p>
            {tool.inputSchema && tool.inputSchema.properties && (
              <SchemaTable schema={tool.inputSchema} />
            )}
          </CardContent>
        </Card>
      ))}
    </div>
      )}
    </div>
  );
}


function ResourcesTab({ configKey }: { configKey: string }) {
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    GatewayServersApi.resources(configKey)
      .then((data) => {
        if (!cancelled) setResources(Array.isArray(data) ? data : []);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(apiError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [configKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
        <div className="w-4 h-4 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
        <span className="text-sm">Loading resources…</span>
      </div>
    );
  }
  if (error) return <ErrorState message={error} />;
  if (resources.length === 0) return <EmptyState message="No resources registered" />;

  const filtered = priorityFilter(resources, search, (r) => r.name);

  return (
    <div className="space-y-4 mt-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <input
          type="text"
          placeholder={`Search ${resources.length} resources...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2.5 bg-muted/60 border border-border rounded-lg text-xs text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-primary/50 transition-colors"
        />
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No resources matching "{search}"</p>
      ) : (
      <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
      {filtered.map((res) => (
        <Card key={res.name} className="bg-card border-border hover:border-border transition-all">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-mono text-teal-400 flex items-center gap-2">
              <Database className="w-3.5 h-3.5 shrink-0" />
              {res.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-1.5">
            <p className="text-xs text-muted-foreground">{res.description || "No description"}</p>
            {res.uri && (
              <p className="text-xs text-muted-foreground">
                URI:{" "}
                <code className="text-primary font-mono">{res.uri}</code>
              </p>
            )}
            {res.mimeType && (
              <p className="text-xs text-muted-foreground">
                MIME:{" "}
                <code className="text-foreground/80 font-mono">{res.mimeType}</code>
              </p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
      )}
    </div>
  );
}


function PromptsTab({ configKey }: { configKey: string }) {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    GatewayServersApi.prompts(configKey)
      .then((data) => {
        if (!cancelled) setPrompts(Array.isArray(data) ? data : []);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(apiError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [configKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
        <div className="w-4 h-4 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
        <span className="text-sm">Loading prompts…</span>
      </div>
    );
  }
  if (error) return <ErrorState message={error} />;
  if (prompts.length === 0) return <EmptyState message="No prompts registered" />;

  const filtered = priorityFilter(prompts, search, (p) => p.name);

  return (
    <div className="space-y-4 mt-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <input
          type="text"
          placeholder={`Search ${prompts.length} prompts...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2.5 bg-muted/60 border border-border rounded-lg text-xs text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-primary/50 transition-colors"
        />
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No prompts matching "{search}"</p>
      ) : (
      <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
      {filtered.map((p) => (
        <Card key={p.name} className="bg-card border-border hover:border-border transition-all">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-mono text-violet-400 flex items-center gap-2">
              <MessageSquare className="w-3.5 h-3.5 shrink-0" />
              {p.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-xs text-muted-foreground mb-2">{p.description || "No description"}</p>
            {p.arguments && p.arguments.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1.5 font-medium">Arguments:</p>
                <ul className="space-y-1">
                  {p.arguments.map((arg) => (
                    <li key={arg.name} className="flex items-start gap-1.5 text-xs">
                      <code className="text-primary font-mono shrink-0">{arg.name}</code>
                      {arg.required && (
                        <Badge className="bg-red-500/15 text-red-400 border-red-500/30 text-xs border py-0 px-1 shrink-0">
                          req
                        </Badge>
                      )}
                      {arg.description && (
                        <span className="text-muted-foreground">– {arg.description}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
      )}
    </div>
  );
}


function ServerDetailView({
  server,
  onBack,
}: {
  server: ServerInfo;
  onBack: () => void;
}) {
  const displayName =
    server.serverName || server.configName || server.configKey;
  const version = server.version || server.serverVersion || "-";

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="text-muted-foreground hover:text-foreground flex items-center gap-1.5"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Servers
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
          <Server className="w-5 h-5 text-primary" />
          <ServerNameDisplay name={displayName} />
        </h1>
        <code className="text-xs text-muted-foreground font-mono bg-muted/60 px-2 py-0.5 rounded">
          {server.configKey}
        </code>
        <ConnectionBadge active={server.active} />
        {version && version !== "-" && (
          <Badge className="bg-muted/60 text-muted-foreground border-border border text-xs">
            v{version}
          </Badge>
        )}
      </div>

      <Tabs defaultValue="tools" className="w-full">
        <TabsList className="bg-muted/60 border border-border">
          <TabsTrigger
            value="tools"
            className="data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:shadow-sm text-muted-foreground flex items-center gap-1.5"
          >
            <Wrench className="w-3.5 h-3.5" />
            Tools
            {(server.toolCount ?? 0) > 0 && (
              <Badge className="bg-primary/20 text-primary border-none text-xs ml-1 px-1.5 py-0">
                {server.toolCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="resources"
            className="data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:shadow-sm text-muted-foreground flex items-center gap-1.5"
          >
            <Database className="w-3.5 h-3.5" />
            Resources
            {(server.resourceCount ?? 0) > 0 && (
              <Badge className="bg-teal-500/20 text-teal-400 border-none text-xs ml-1 px-1.5 py-0">
                {server.resourceCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="prompts"
            className="data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:shadow-sm text-muted-foreground flex items-center gap-1.5"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            Prompts
            {(server.promptCount ?? 0) > 0 && (
              <Badge className="bg-violet-500/20 text-violet-400 border-none text-xs ml-1 px-1.5 py-0">
                {server.promptCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tools" className="mt-4">
          <ToolsTab configKey={server.configKey} />
        </TabsContent>
        <TabsContent value="resources" className="mt-4">
          <ResourcesTab configKey={server.configKey} />
        </TabsContent>
        <TabsContent value="prompts" className="mt-4">
          <PromptsTab configKey={server.configKey} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function ServerCard({
  server,
  onSelect,
  onConnect,
  onDisconnect,
  onReconnect,
}: {
  server: ServerInfo;
  onSelect: (s: ServerInfo) => void;
  onConnect: (key: string) => Promise<void>;
  onDisconnect: (key: string) => Promise<void>;
  onReconnect: (key: string) => Promise<void>;
}) {
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  const displayName =
    server.serverName || server.configName || server.configKey;
  const version = server.version || server.serverVersion;
  const lastPulse = server.lastActivity ? timeAgo(server.lastActivity) : "No activity";

  const handleConnect = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setConnecting(true);
    try {
      await onConnect(server.configKey);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setDisconnecting(true);
    try {
      await onDisconnect(server.configKey);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleReconnect = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setReconnecting(true);
    try {
      await onReconnect(server.configKey);
    } finally {
      setReconnecting(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-border bg-card shadow-sm hover:border-border hover:bg-card transition-all cursor-pointer group overflow-hidden"
      onClick={() => onSelect(server)}
    >
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 text-primary shrink-0">
              <Server className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-foreground text-base truncate">{displayName}</h3>
            </div>
          </div>
          <span className={cn(
            "inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full border shrink-0",
            server.active
              ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
              : "bg-red-500/15 text-red-400 border-red-500/30"
          )}>
            <span className={cn("w-1.5 h-1.5 rounded-full", server.active ? "bg-emerald-400" : "bg-red-400")} />
            {server.active ? "CONNECTED" : "DISCONNECTED"}
          </span>
        </div>
      </div>

      <div className="flex gap-2 px-5 pb-4">
        {[
          { label: "Tools", value: server.toolCount ?? 0, active: (server.toolCount ?? 0) > 0 },
          { label: "Resources", value: server.resourceCount ?? 0, active: (server.resourceCount ?? 0) > 0 },
          { label: "Prompts", value: server.promptCount ?? 0, active: (server.promptCount ?? 0) > 0 },
        ].map((cap) => (
          <div
            key={cap.label}
            className={cn(
              "flex-1 rounded-lg bg-muted/60 border border-border px-3 py-3 relative overflow-hidden",
              ""
            )}
          >
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{cap.label}</p>
            <p className={cn("text-2xl font-bold", cap.active ? "text-foreground" : "text-muted-foreground/50")}>{cap.value}</p>
          </div>
        ))}
      </div>

      <div className="mx-5 mb-4 space-y-0">
        <div className="flex items-center justify-between py-2.5 border-b border-border">
          <span className="text-xs text-muted-foreground">Config Key</span>
          <span className="text-xs font-mono text-primary">{server.configKey}</span>
        </div>
        <div className="flex items-center justify-between py-2.5 border-b border-border">
          <span className="text-xs text-muted-foreground">Last Pulse</span>
          <span className="text-xs text-foreground/60 italic">{lastPulse}</span>
        </div>
        {version && (
          <div className="pt-3">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium mb-1.5">Server Version</p>
            <div className="bg-primary/5 border border-primary/10 rounded-lg px-3 py-2">
              <p className="text-xs font-mono text-foreground/70 truncate">{version}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


export default function GatewayServersPage() {
  const { toast } = useToast();
  const [servers, setServers] = useState<ServerInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedServer, setSelectedServer] = useState<ServerInfo | null>(null);
  const [serverSearch, setServerSearch] = useState("");

  const loadServers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await GatewayServersApi.list();
      const parsed: ServerInfo[] = Object.entries(data || {}).map(
        ([configKey, info]: [string, any]) => ({
          ...info,
          configKey,
          active: info.status === "connected" || info.status === "CONNECTED",
          version: info.serverVersion || info.version || "",
          resourceCount: info.resourceCount || 0,
          promptCount: info.promptCount || 0,
          toolCount: info.toolCount || 0,
        })
      );
      setServers(parsed);
    } catch (e: any) {
      setError(apiError(e));
      toast({
        title: "Failed to load servers",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  const handleConnect = async (key: string) => {
    try {
      await GatewayServerConfigApi.connect(key);
      toast({
        title: "Server connected",
        description: `'${key}' is now connected`,
      });
      await loadServers();
    } catch (e: any) {
      toast({
        title: "Connect failed",
        description: apiError(e),
        variant: "destructive",
      });
    }
  };

  const handleDisconnect = async (key: string) => {
    try {
      await GatewayServerConfigApi.disconnect(key);
      toast({
        title: "Server disconnected",
        description: `'${key}' has been disconnected`,
      });
      await loadServers();
    } catch (e: any) {
      toast({
        title: "Disconnect failed",
        description: apiError(e),
        variant: "destructive",
      });
    }
  };

  const handleReconnect = async (key: string) => {
    try {
      await GatewayServerConfigApi.reconnect(key);
      toast({
        title: "Server reconnected",
        description: `'${key}' has been reconnected`,
      });
      await loadServers();
    } catch (e: any) {
      toast({
        title: "Reconnect failed",
        description: apiError(e),
        variant: "destructive",
      });
    }
  };

  const handleSelectServer = (server: ServerInfo) => {
    setSelectedServer(server);
  };

  const handleBack = () => {
    setSelectedServer(null);
  };

  if (selectedServer) {
    return (
      <div className="p-6">
        <ServerDetailView server={selectedServer} onBack={handleBack} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Server className="w-5 h-5 text-primary" />
            MCP Servers
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Connected MCP servers and their capabilities
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="border-border text-muted-foreground hover:text-foreground"
          onClick={loadServers}
          disabled={loading}
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-muted-foreground border-t-foreground/80 rounded-full animate-spin mr-1.5" />
          ) : (
            <RotateCw className="w-4 h-4 mr-1.5" />
          )}
          Refresh
        </Button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-24 gap-2 text-muted-foreground">
          <div className="w-6 h-6 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
          <span>Loading servers…</span>
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <AlertTriangle className="w-10 h-10 text-amber-500" />
          <p className="text-muted-foreground text-sm font-medium">Failed to load servers</p>
          <p className="text-muted-foreground text-xs font-mono">{error}</p>
          <Button size="sm" variant="outline" onClick={loadServers}>
            Try Again
          </Button>
        </div>
      )}

      {!loading && !error && servers.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Server className="w-12 h-12 text-muted-foreground/70" />
          <p className="text-muted-foreground text-sm font-medium">No MCP servers connected</p>
          <p className="text-muted-foreground text-xs">
            Go to Configuration to add and connect servers
          </p>
        </div>
      )}

      {!loading && !error && servers.length > 0 && (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Badge className="bg-muted/60 text-foreground/80 border-border border text-xs">
                {servers.length} server{servers.length !== 1 ? "s" : ""}
              </Badge>
              <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 border text-xs">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                {servers.filter((s) => s.active).length} connected
              </Badge>
              {servers.filter((s) => !s.active).length > 0 && (
                <Badge className="bg-red-500/15 text-red-400 border-red-500/30 border text-xs">
                  <XCircle className="w-3 h-3 mr-1" />
                  {servers.filter((s) => !s.active).length} disconnected
                </Badge>
              )}
            </div>
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search servers..."
                value={serverSearch}
                onChange={(e) => setServerSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 bg-muted/60 border border-border rounded-lg text-xs text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-primary/50 transition-colors"
              />
            </div>
          </div>

          {(() => {
            const filteredServers = (() => {
              if (!serverSearch.trim()) return servers;
              const q = serverSearch.toLowerCase().trim();
              const startsWith: typeof servers = [];
              const contains: typeof servers = [];
              for (const s of servers) {
                const name = (s.serverName || s.configName || "").toLowerCase();
                const fields = [name, (s.configKey || "").toLowerCase(), (s.version || "").toLowerCase()];
                if (name.startsWith(q)) startsWith.push(s);
                else if (fields.some((f) => f.includes(q))) contains.push(s);
              }
              return [...startsWith, ...contains];
            })();
            return filteredServers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-12">No servers matching "{serverSearch}"</p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {filteredServers.map((server) => (
                  <ServerCard
                    key={server.configKey}
                    server={server}
                    onSelect={handleSelectServer}
                    onConnect={handleConnect}
                    onDisconnect={handleDisconnect}
                    onReconnect={handleReconnect}
                  />
                ))}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
